// "Set up in eRound + OCPP" (CWA-231): one approval that confirms the access
// data, promotes the customer and sends every waiting station to eRound, in that
// order. Promoting first means a clearing failure never leaves a station in
// eRound for a customer clearing doesn't know. A failed step stops the run; the
// next run skips whatever is already done, because the plan is read from the
// prospect's stage and the stations' current eRound status.

import { StationProvisioning } from "../hooks/useStationProvisioning";
import { approveProvisioning } from "../services/eroundProvisioning";
import { promoteProspect, registerProspectStations } from "../services/onboarding/prospects";
import { ProvisioningDetail } from "../types/eroundProvisioning";
import { OnboardingState, Prospect } from "../types/onboarding";
import { promoteErrorMessage, registerStationsErrorMessage } from "../pages/Onboarding/Prospects/components/stationConfigErrors";
import { provisioningErrorMessage } from "../pages/Onboarding/Prospects/components/provisioningErrors";
import { canRegisterStations } from "./onboardingStates";
import { isCustomerPromoted } from "./setupFee";

export type EroundSetupStep = "access-data" | "promote" | "approve";

export interface EroundSetupPlan {
  accessDataDone: boolean;
  promoted: boolean;
  // Stations waiting for approval, with the fingerprint the approval must quote.
  approvals: { stationId: string; record: ProvisioningDetail; fingerprint: string }[];
  // Stations eRound won't get from this approval: untracked, rejected or unreadable.
  byHand: string[];
}

export interface EroundSetupResult {
  failedAt: EroundSetupStep | null;
  message: string | null;
  approved: string[];
}

const BY_HAND = ["REJECTED"];

export const setupPlan = (
  state: OnboardingState,
  stationIds: string[],
  byStation: Record<string, StationProvisioning>
): EroundSetupPlan => {
  const approvals: EroundSetupPlan["approvals"] = [];
  const byHand: string[] = [];
  stationIds.forEach((stationId) => {
    const entry = byStation[stationId];
    if (entry?.state === "unavailable") byHand.push(stationId);
    if (entry?.state !== "loaded") return;
    const record = entry.record;
    if (!record || BY_HAND.includes(record.status)) {
      byHand.push(stationId);
    } else if (record.status === "AWAITING_APPROVAL" && record.fingerprint) {
      approvals.push({ stationId, record, fingerprint: record.fingerprint });
    }
  });
  return {
    accessDataDone: !canRegisterStations(state),
    promoted: isCustomerPromoted(state),
    approvals,
    byHand,
  };
};

export const runEroundSetup = async (prospectId: string, plan: EroundSetupPlan): Promise<EroundSetupResult> => {
  const approved: string[] = [];
  const failed = (failedAt: EroundSetupStep, message: string): EroundSetupResult => ({ failedAt, message, approved });

  if (!plan.accessDataDone) {
    try {
      await registerProspectStations(prospectId);
    } catch (err) {
      return failed("access-data", registerStationsErrorMessage(err));
    }
  }
  if (!plan.promoted) {
    try {
      await promoteProspect(prospectId);
    } catch (err) {
      return failed("promote", promoteErrorMessage(err));
    }
  }
  for (const { stationId, fingerprint } of plan.approvals) {
    try {
      await approveProvisioning(stationId, fingerprint);
      approved.push(stationId);
    } catch (err: any) {
      const reason =
        err?.response?.status === 409
          ? "it changed since the page was loaded. Check it again"
          : provisioningErrorMessage(err, "approve");
      return failed("approve", `${stationId} was not approved: ${reason}.`);
    }
  }
  return { failedAt: null, message: null, approved };
};

type ClearingSource = Partial<Pick<Prospect, "prospect_type" | "existing_customer_prospect_id">> &
  Pick<Prospect, "company_name" | "first_name" | "last_name">;

/** Who the customer becomes in clearing when promoted. */
export const clearingCustomerText = (prospect: ClearingSource): string => {
  if (prospect.prospect_type === "ADDITIONAL_STATION") {
    const from = prospect.existing_customer_prospect_id ? ` (from prospect ${prospect.existing_customer_prospect_id})` : "";
    return `These stations join the existing customer in clearing${from}.`;
  }
  const name = prospect.company_name?.trim() || `${prospect.first_name} ${prospect.last_name}`.trim();
  return `Promoting creates ${name} as a new customer in clearing.`;
};
